import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { Calendar, Clock, MapPin, Users, DollarSign, Music, Tag, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';

const EventModal = ({ isOpen, onClose, event, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    startTime: '',
    endTime: '',
    venueId: '',
    venueName: '',
    type: 'night',
    status: 'planning',
    capacity: '',
    ticketPrice: '',
    description: '',
    lineup: [],
    tags: []
  });

  const [venues, setVenues] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingVenues, setLoadingVenues] = useState(true);

  const eventTypes = [
    { value: 'breakfast', label: 'Breakfast Club', icon: '☀️' },
    { value: 'sunset', label: 'Sunset Session', icon: '🌅' },
    { value: 'night', label: 'Night Event', icon: '🌙' },
    { value: 'festival', label: 'Festival', icon: '🎪' },
    { value: 'special', label: 'Special Event', icon: '✨' }
  ];

  const eventStatuses = [
    { value: 'concept', label: 'Concept' },
    { value: 'planning', label: 'Planning' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'onsale', label: 'On Sale' },
    { value: 'soldout', label: 'Sold Out' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    loadVenues();
  }, []);

  useEffect(() => {
    if (event) {
      setFormData({
        name: event.name || '',
        date: event.date || '',
        startTime: event.startTime || '',
        endTime: event.endTime || '',
        venueId: event.venueId || '',
        venueName: event.venueName || event.venue || '',
        type: event.type || 'night',
        status: event.status || 'planning',
        capacity: event.capacity || '',
        ticketPrice: event.ticketPrice || '',
        description: event.description || '',
        lineup: event.lineup || [],
        tags: event.tags || []
      });
    } else {
      // Reset form for new event
      setFormData({
        name: '',
        date: '',
        startTime: '',
        endTime: '',
        venueId: '',
        venueName: '',
        type: 'night',
        status: 'planning',
        capacity: '',
        ticketPrice: '',
        description: '',
        lineup: [],
        tags: []
      });
    }
    setErrors({});
  }, [event]);

  const loadVenues = async () => {
    try {
      setLoadingVenues(true);
      const response = await api.venues.getAll();
      setVenues(response.venues);
    } catch (error) {
      console.error('Error loading venues:', error);
    } finally {
      setLoadingVenues(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // If venue is selected, update venue name
    if (name === 'venueId') {
      const selectedVenue = venues.find(v => v.id === value);
      if (selectedVenue) {
        setFormData(prev => ({
          ...prev,
          venueName: selectedVenue.name,
          capacity: selectedVenue.capacity || prev.capacity
        }));
      }
    }
  };

  const handleLineupChange = (index, value) => {
    const newLineup = [...formData.lineup];
    newLineup[index] = value;
    setFormData(prev => ({ ...prev, lineup: newLineup }));
  };

  const addLineupArtist = () => {
    setFormData(prev => ({ ...prev, lineup: [...prev.lineup, ''] }));
  };

  const removeLineupArtist = (index) => {
    setFormData(prev => ({
      ...prev,
      lineup: prev.lineup.filter((_, i) => i !== index)
    }));
  };

  const handleTagInput = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const tag = e.target.value.trim();
      if (!formData.tags.includes(tag)) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      }
      e.target.value = '';
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Event name is required';
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.startTime) newErrors.startTime = 'Start time is required';
    if (!formData.venueId && !formData.venueName) newErrors.venue = 'Venue is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    try {
      // Combine date and time
      const eventData = {
        ...formData,
        time: formData.startTime + (formData.endTime ? ` - ${formData.endTime}` : ''),
        venue: formData.venueName,
        lineup: formData.lineup.filter(artist => artist.trim())
      };
      
      await onSave(eventData);
      onClose();
    } catch (error) {
      console.error('Error saving event:', error);
      setErrors({ submit: error.message || 'Failed to save event' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={event ? 'Edit Event' : 'Create New Event'}
      maxWidth="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">Event Details</h4>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              Event Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`input-dark w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="e.g., The Breakfast Club - Summer Edition"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Event Type
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="input-dark w-full"
              >
                {eventTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="input-dark w-full"
              >
                {eventStatuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="input-dark w-full"
              placeholder="Describe your event..."
            />
          </div>
        </div>

        {/* Date & Time */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">Date & Time</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                <Calendar size={16} className="inline mr-1" />
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className={`input-dark w-full ${errors.date ? 'border-red-500' : ''}`}
              />
              {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Clock size={16} className="inline mr-1" />
                Start Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                className={`input-dark w-full ${errors.startTime ? 'border-red-500' : ''}`}
              />
              {errors.startTime && <p className="text-red-500 text-sm mt-1">{errors.startTime}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Clock size={16} className="inline mr-1" />
                End Time
              </label>
              <input
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
                className="input-dark w-full"
              />
            </div>
          </div>
        </div>

        {/* Venue & Capacity */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">Venue & Capacity</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                <MapPin size={16} className="inline mr-1" />
                Venue <span className="text-red-500">*</span>
              </label>
              {loadingVenues ? (
                <div className="input-dark w-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#D4A574]"></div>
                </div>
              ) : (
                <select
                  name="venueId"
                  value={formData.venueId}
                  onChange={handleChange}
                  className={`input-dark w-full ${errors.venue ? 'border-red-500' : ''}`}
                >
                  <option value="">Select a venue</option>
                  {venues.map(venue => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              )}
              {errors.venue && <p className="text-red-500 text-sm mt-1">{errors.venue}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Users size={16} className="inline mr-1" />
                Capacity
              </label>
              <input
                type="text"
                name="capacity"
                value={formData.capacity}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="e.g., 500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              <DollarSign size={16} className="inline mr-1" />
              Ticket Price
            </label>
            <input
              type="text"
              name="ticketPrice"
              value={formData.ticketPrice}
              onChange={handleChange}
              className="input-dark w-full"
              placeholder="e.g., €15-25"
            />
          </div>
        </div>

        {/* Lineup */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">
            <Music size={16} className="inline mr-1" />
            Lineup
          </h4>
          
          {formData.lineup.map((artist, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={artist}
                onChange={(e) => handleLineupChange(index, e.target.value)}
                className="input-dark flex-1"
                placeholder="Artist name"
              />
              <button
                type="button"
                onClick={() => removeLineupArtist(index)}
                className="btn-secondary text-red-500 hover:bg-red-500/10"
              >
                Remove
              </button>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addLineupArtist}
            className="btn-secondary text-sm"
          >
            Add Artist
          </button>
        </div>

        {/* Tags */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">
            <Tag size={16} className="inline mr-1" />
            Tags
          </h4>
          
          <input
            type="text"
            onKeyDown={handleTagInput}
            className="input-dark w-full"
            placeholder="Type tag and press Enter"
          />
          
          <div className="flex flex-wrap gap-2">
            {formData.tags.map(tag => (
              <span
                key={tag}
                className="px-3 py-1 bg-[#0A0A0A] rounded-full text-sm flex items-center gap-2"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="text-[#888888] hover:text-white"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
            <AlertCircle size={20} className="text-red-500" />
            <p className="text-red-500 text-sm">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-4 border-t border-[#333333]">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : event ? 'Save Changes' : 'Create Event'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EventModal;